import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from 'react'
import { Modal } from 'antd'
import type { ModalFuncProps } from 'antd/lib'

export type ModalType = 'info' | 'success' | 'error' | 'warning' | 'confirm'

interface AppModalContextValue {
  show: (type: ModalType, opts: ModalFuncProps) => void
  info: (opts: ModalFuncProps) => void
  success: (opts: ModalFuncProps) => void
  error: (opts: ModalFuncProps) => void
  warning: (opts: ModalFuncProps) => void
  confirm: (opts: ModalFuncProps) => Promise<boolean>
}

const AppModalContext = createContext<AppModalContextValue | null>(null)

export const AppModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modal, contextHolder] = Modal.useModal()

  const show = useCallback((type: ModalType, options: ModalFuncProps) => {
    modal[type]({
      centered: options.centered ?? true,
      width: options.width ?? 'auto',
      maskClosable: options.maskClosable ?? false,
      getContainer: options.getContainer ?? (() => document.body),
      ...options,
    })
  }, [modal])

  const contextValue = useMemo<AppModalContextValue>(
    () => ({
      show,
      info: opts => show('info', opts),
      success: opts => show('success', opts),
      error: opts => show('error', opts),
      warning: opts => show('warning', opts),
      confirm: async opts => modal.confirm(opts),
    }),
    [modal, show],
  )

  return (
    <AppModalContext.Provider value={contextValue}>
      {children}
      {contextHolder}
    </AppModalContext.Provider>
  )
}

export const useAppModal = (): AppModalContextValue => {
  const ctx = useContext(AppModalContext)
  if (!ctx) throw new Error('useAppModal must be used within AppModalProvider')
  return ctx
}
